// deps
import { useRef, useEffect, forwardRef } from "react";
import { Box, Button, Icon, Text } from "@chakra-ui/react";
import { FormattedMessage, useIntl } from "react-intl";
import PropTypes from "prop-types";
import { IconButton } from "@chakra-ui/react";
import { IoTrashOutline } from "react-icons/io5";
import { useColorModeValue } from "@chakra-ui/react";
import useCombinedRefs from "../../../hooks/useCombinedRefs";

/**
 * Retourne la taille de la police d’écriture à utiliser en fonction de la taille demandée
 * @param {object} param0
 * @param {object} param0.size
 * @return {string}
 */
function getFontSize({ size }) {
  switch (size) {
    case "xl":
      return "1.5rem";

    case "lg":
      return "1.25rem";

    default:
    case "md":
      return "1rem";

    case "sm":
      return "0.875rem";

    case "xs":
      return "0.75rem";
  }
}

const InputFile = forwardRef(
  /**
   * @param {{
   * name: string,
   * value: any,
   * onChange?: (event: {target: {name: string, value: any}}) => void,
   * placeholder?: string,
   * accept?: string,
   * size?: "xs"  | "sm"  | "md"  | "lg"  | "xl",
   * shouldRenderDelete?: boolean,
   * isDisabled?: boolean,
   * isMultiple?: boolean,
   * }} props
   */
  function InputFile(props, ref) {
    const {
      name,
      value,
      onChange,
      placeholder,
      accept,
      size,
      shouldRenderDelete = false,
      isDisabled,
      isMultiple = false,
    } = props;

    const intl = useIntl();

    const $local = useRef();

    const $file = useCombinedRefs($local, ref);

    /**
     * Gère le clic sur le bouton d’ajout d’un fichier
     */
    function handleAddFile(event) {
      event.preventDefault();

      $file.current.click();
    }

    /**
     * Gère la suppression d’un fichier
     */
    function handleDeleteFile(event) {
      event.preventDefault();
      event.stopPropagation();

      onChange?.({
        target: {
          name,
          value: null,
        },
      });
    }

    useEffect(
      function () {
        if ($file.current instanceof HTMLInputElement) {
          if (
            // Si la valeur du champ devient vide, on nettoie le champ input
            !value
          ) {
            $file.current.value = null;
          }
        }
      },
      [$file, value],
    );

    useEffect(
      function () {
        if ($file.current instanceof HTMLInputElement) {
          const handleChange = function (event) {
            const value = isMultiple
              ? Array.from(event.target.files)
              : Array.from(event.target.files)[0];

            onChange?.({
              target: {
                name,
                value,
              },
            });
          };

          const $currentFile = $file.current;
          $currentFile.addEventListener("change", handleChange);
          return function () {
            $currentFile.removeEventListener("change", handleChange);
          };
        }
      },
      [$file, isMultiple, name, onChange],
    );

    return (
      <Box w="full">
        <input
          ref={$file}
          style={{
            width: 0,
            height: 0,
            visibility: "hidden",
            position: "absolute",
          }}
          type="file"
          name={name}
          disabled={isDisabled}
          accept={accept}
          multiple={isMultiple}
        />

        <Box
          display="flex"
          borderRadius="0.375rem"
          overflow="hidden"
          w="full"
          onClick={handleAddFile}>
          <Box
            cursor="pointer"
            paddingLeft="0.625rem"
            paddingRight="0.625rem"
            display="flex"
            alignItems="center"
            minW="12.5rem"
            borderStyle="solid"
            borderColor={useColorModeValue("gray.200", "gray.600")}
            borderWidth="0.0625rem 0 0.0625rem 0.0625rem"
            borderLeftRadius="0.375rem"
            flexGrow={1}>
            <Text
              display="inline"
              whiteSpace="nowrap"
              overflow="hidden"
              fontSize={getFontSize({ size })}
              textOverflow="ellipsis">
              {(() => {
                if (Array.isArray(value) && value.length > 0) {
                  return value
                    .map((file) => file?.name)
                    .filter((name) => name)
                    .join(", ");
                }

                if (!Array.isArray(value) && value !== null) {
                  return value?.name;
                }

                return (
                  placeholder ??
                  intl.formatMessage({
                    defaultMessage: "Aucun fichier sélectionné",
                  })
                );
              })()}
            </Text>
          </Box>

          <Button
            size={size}
            colorScheme="gray"
            variant="outline"
            borderLeftRadius={0}
            flexShrink={0}
            borderRightRadius={shouldRenderDelete ? 0 : undefined}
            isDisabled={isDisabled}>
            {isMultiple ? (
              <FormattedMessage defaultMessage="Choisir les fichiers" />
            ) : (
              <FormattedMessage defaultMessage="Choisir un fichier" />
            )}
          </Button>

          {shouldRenderDelete && (
            <IconButton
              borderLeftRadius={0}
              size={size}
              colorScheme="red"
              aria-label={intl.formatMessage({
                defaultMessage: "Supprimer le fichier",
              })}
              icon={<Icon as={IoTrashOutline} />}
              isDisabled={isDisabled || !value}
              onClick={handleDeleteFile}
            />
          )}
        </Box>
      </Box>
    );
  },
);

InputFile.propTypes = {
  placeholder: PropTypes.string,
  accept: PropTypes.string,
  onChange: PropTypes.func,
  shouldRenderDelete: PropTypes.bool,
  isDisabled: PropTypes.bool,
};

export default InputFile;
